import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Chip
} from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import PINValidator from './PINValidator';
import {
  parsePEMPrivateKey,
  parseDERPrivateKey,
  buildOpenPGPImportAPDU,
  validateKeyData
} from './keyParser';
import { transceive } from './actions';

/**
 * OpenPGP Key Import Dialog
 * Handles file upload, parsing, and import of private keys to OpenPGP key types
 */

const OpenPGPKeyImportDialog = ({ open, onClose, keyType, keyTypeName, dispatch, onImportSuccess }) => {
  const [file, setFile] = useState(null);
  const [keyInfo, setKeyInfo] = useState(null);
  const [adminPin, setAdminPin] = useState('');
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setError('');
    setKeyInfo(null);

    try {
      const fileContent = await selectedFile.text();

      // Detect file format and parse
      let parsedKey;
      if (selectedFile.name.endsWith('.pem') || fileContent.includes('BEGIN')) {
        parsedKey = await parsePEMPrivateKey(fileContent);
      } else if (selectedFile.name.endsWith('.der')) {
        const arrayBuffer = await selectedFile.arrayBuffer();
        parsedKey = await parseDERPrivateKey(arrayBuffer);
      } else {
        // Try PEM first, then DER
        try {
          parsedKey = await parsePEMPrivateKey(fileContent);
        } catch (e) {
          const arrayBuffer = await selectedFile.arrayBuffer();
          parsedKey = await parseDERPrivateKey(arrayBuffer);
        }
      }

      // Validate key
      const validation = validateKeyData(parsedKey);
      if (!validation.valid) {
        throw new Error(validation.error);
      }

      setKeyInfo(parsedKey);
    } catch (error) {
      console.error('File parsing error:', error);
      setError('Failed to parse key file: ' + error.message);
    }
  };

  const handleImport = async () => {
    if (!keyInfo || !adminPin) {
      setError('Please select a file and enter Admin PIN');
      return;
    }

    setImporting(true);
    setProgress(0);
    setError('');

    try {
      // Step 1: Select OpenPGP applet
      setProgress(20);
      await dispatch(transceive('00A4040006D27600012401'));

      // Step 2: Verify Admin PIN (PW3)
      setProgress(40);
      const pinHex = adminPin.split('').map(c => c.charCodeAt(0).toString(16).padStart(2, '0')).join('');
      const verifyAPDU = `00200083${(pinHex.length / 2).toString(16).padStart(2, '0')}${pinHex}`;
      const verifyRes = await dispatch(transceive(verifyAPDU));

      if (!verifyRes.endsWith('9000')) {
        const sw = verifyRes.slice(-4);
        if (sw.startsWith('63C')) {
          const remaining = parseInt(sw.slice(3), 16);
          throw new Error(`Admin PIN verification failed. ${remaining} attempts remaining.`);
        }
        throw new Error('Admin PIN verification failed');
      }

      // Step 3: Build and send import APDU
      setProgress(60);
      const importAPDU = buildOpenPGPImportAPDU(keyInfo, keyType);
      const response = await dispatch(transceive(importAPDU));

      setProgress(80);

      if (response.endsWith('9000')) {
        setProgress(100);
        setSuccess(true);

        // Notify parent
        if (onImportSuccess) {
          onImportSuccess(keyType, keyInfo);
        }
      } else {
        throw new Error(`Import failed with status: ${response.slice(-4)}`);
      }
    } catch (error) {
      console.error('Import error:', error);
      setError(error.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    if (!importing) {
      setFile(null);
      setKeyInfo(null);
      setAdminPin('');
      setError('');
      setSuccess(false);
      setProgress(0);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={importing}
    >
      <DialogTitle>
        Import Private Key: {keyTypeName}
      </DialogTitle>
      <DialogContent>
        {!success ? (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>Import External Key</AlertTitle>
              Select a private key file (PEM or DER format) to import as your OpenPGP {keyTypeName}.
            </Alert>

            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>Note</AlertTitle>
              This will replace any existing {keyTypeName} on your card. Make sure you have backed up your old key if needed.
            </Alert>

            {/* File Upload */}
            <Box sx={{ mb: 3 }}>
              <input
                accept=".pem,.der,.key"
                style={{ display: 'none' }}
                id="openpgp-key-file-input"
                type="file"
                onChange={handleFileChange}
                disabled={importing}
              />
              <label htmlFor="openpgp-key-file-input">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                  fullWidth
                  disabled={importing}
                >
                  {file ? file.name : 'Select Key File (.pem, .der)'}
                </Button>
              </label>
            </Box>

            {/* Key Information */}
            {keyInfo && (
              <Paper elevation={2} sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Key Information:
                </Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell><strong>Type</strong></TableCell>
                      <TableCell>{keyInfo.type}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Size</strong></TableCell>
                      <TableCell>{keyInfo.size} bits</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Key Type</strong></TableCell>
                      <TableCell>
                        <Chip
                          label={keyTypeName}
                          size="small"
                          color="primary"
                        />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Paper>
            )}

            {/* Admin PIN Input */}
            <PINValidator
              value={adminPin}
              onChange={(e) => setAdminPin(e.target.value)}
              label="Admin PIN (PW3)"
              type="OpenPGP_ADMIN"
              dispatch={dispatch}
              disabled={importing}
              helperText="Default: 12345678"
            />

            {/* Progress Bar */}
            {importing && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress variant="determinate" value={progress} />
                <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                  {progress < 40 ? 'Verifying Admin PIN...' :
                   progress < 60 ? 'Importing key...' :
                   progress < 100 ? 'Finalizing...' : 'Complete!'}
                </Typography>
              </Box>
            )}

            {/* Error Display */}
            {error && (
              <Alert severity="error" sx={{ mt: 2 }} icon={<ErrorIcon />}>
                <AlertTitle>Import Failed</AlertTitle>
                {error}
              </Alert>
            )}
          </>
        ) : (
          /* Success Display */
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Key Imported Successfully!
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Your {keyTypeName} has been imported to your OpenPGP card.
            </Typography>

            <Alert severity="info" sx={{ mt: 3, textAlign: 'left' }}>
              <AlertTitle>Next Steps</AlertTitle>
              <Typography variant="body2">
                • Use <code>gpg --card-status</code> to verify the import<br />
                • Configure your email client to use the OpenPGP card<br />
                • Remember to backup your private key file securely
              </Typography>
            </Alert>

            <Alert severity="success" sx={{ mt: 2, textAlign: 'left' }}>
              <AlertTitle>Testing Your Key</AlertTitle>
              <Typography variant="body2">
                {keyType === 'sig' && '• Test signing: gpg --clearsign test.txt'}
                {keyType === 'dec' && '• Test decryption: echo "test" | gpg --encrypt -r you@example.com | gpg --decrypt'}
                {keyType === 'aut' && '• Test SSH: Add to ~/.ssh/config and test connection'}
              </Typography>
            </Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {!success ? (
          <>
            <Button onClick={handleClose} disabled={importing}>
              Cancel
            </Button>
            <Button
              onClick={handleImport}
              variant="contained"
              disabled={!keyInfo || !adminPin || importing}
            >
              {importing ? 'Importing...' : 'Import'}
            </Button>
          </>
        ) : (
          <Button onClick={handleClose} variant="contained">
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default OpenPGPKeyImportDialog;
